import { useMutation } from '@tanstack/react-query'
import { AxiosError } from 'axios'

import useAppDispatch from '../../../hooks/useAppDispatch'
import { isNotProduction } from '../../../lib/helpers/envHelpers'
import {
  getErrorDebugInfo,
  isConstraintViolationListError,
} from '../../../lib/helpers/helperFunctions'
import { buildCreateHistoricalReferralRequest } from '../../../lib/helpers/referralImportHelpers'
import clientService from '../../../lib/services/clientService'
import constraintViolationService from '../../../lib/services/constraintViolationService'
import referralService from '../../../lib/services/referralService'
import { ReferralsImportItem } from '../../../redux/slices/referralsImportBase'
import {
  updateImportItem,
  updateImportItemStatus,
} from '../../../redux/slices/referralsImportHistorical'
import { FormErrors } from '../../../types/misc'
import { CreateClientForReferralRequest } from '../../../types/requests/clients'

export function useImportHistoricalReferralMutation() {
  const dispatch = useAppDispatch()

  return useMutation({
    // retry mutation up to 4 times with linear backoff delay (last delay 3000 ms, total time 7.5 s)
    retry: 4,
    retryDelay: (attempt) => attempt * 750,
    mutationFn: async (importItem: ReferralsImportItem) => {
      dispatch(
        updateImportItemStatus({
          refUUID: importItem.refUUID,
          importStatus: 'processing',
        }),
      )

      // create a user client if missing
      // autogeneratedClientEmail is only set when source email is not present
      const newClientEmail =
        importItem.autogeneratedClientEmail || importItem.sourceClientEmail

      // - userCompanyClient is needed in all cases
      let userCompanyClient = importItem.userCompanyClient

      if (userCompanyClient === null) {
        // create a client if missing
        userCompanyClient =
          await clientService.createClientForReferralOrFindExisting(
            buildCreateClientRequest(
              importItem.sourceClientFirstName,
              importItem.sourceClientLastName,
              newClientEmail,
            ),
            importItem.userCompany.id,
          )

        // update import data state
        dispatch(
          updateImportItem({
            refUUID: importItem.refUUID,
            userCompanyClient: userCompanyClient,
          }),
        )
      } else {
        // eventually update existing client
        if (importItem.onClientDuplicate === 'update') {
          await clientService.updateClientForReferral(
            userCompanyClient.id,
            buildCreateClientRequest(
              importItem.sourceClientFirstName,
              importItem.sourceClientLastName,
              importItem.sourceClientEmail,
            ),
          )
        }
      }

      // import with resolved clients
      const request = buildCreateHistoricalReferralRequest({
        ...importItem,
        userCompanyClient: userCompanyClient,
      })

      return referralService.createReferral(request)
    },
    onError: async (error, importItem) => {
      let formattedErrors: FormErrors | undefined

      if (isConstraintViolationListError(error)) {
        formattedErrors = constraintViolationService.formatErrors(error)
      } else {
        if (error instanceof AxiosError) {
          formattedErrors = {
            error: error.response?.data?.detail ?? error.message,
          }
        } else if (error.message) {
          formattedErrors = {
            error: error.message.replace('Invariant failed: ', ''),
          }
        } else {
          formattedErrors = {
            error: 'An error occurred',
          }
        }

        if (isNotProduction()) {
          formattedErrors.debug = JSON.stringify(getErrorDebugInfo(error))
        }
      }

      dispatch(
        updateImportItemStatus({
          refUUID: importItem.refUUID,
          importStatus: 'error',
          importErrors: formattedErrors,
        }),
      )
    },
    onSuccess: async (_data, importItem) => {
      dispatch(
        updateImportItemStatus({
          refUUID: importItem.refUUID,
          importStatus: 'imported',
          importErrors: undefined,
        }),
      )
    },
  })
}

function buildCreateClientRequest(
  firstName: string,
  lastName: string,
  email: string,
): CreateClientForReferralRequest {
  return {
    firstName: firstName,
    lastName: lastName,
    email: email,
    clientId: '',
  }
}
